// The pricing facts the wizard has to render, mirrored from
// functions/lib/plans.js and checked against it in plans.parity.test.ts.
//
// The dashboard reads live prices out of the billing response, so changing a
// price stays a server change. The constants below exist for the one surface
// that has no billing response to read: the public pricing section, which
// renders for a visitor who has not signed in and cannot call the API. A
// public page quoting a price Stripe does not charge is the kind of mistake
// that ends in a chargeback, so parity.test.ts pins all of these against
// functions/lib/plans.js.

// A free bot's answers per month, shared by every visitor (since 2026-09-27;
// it replaced a per-visitor daily cap). Mirrored because the pricing card, the
// wizard and the Plan card all have to say it.
export const FREE_ANSWERS_PER_MONTH = 200;

// A ceiling per conversation on a free bot, so one visitor cannot spend the
// month in one sitting. High enough that nobody asking real questions meets it.
export const FREE_QUESTIONS_PER_SESSION = 20;

// The paid plans (since 2026-10-01): each includes answers a month, and past
// that extra answers are billed per 1,000, up to the owner's spending limit.
// Mirrored for the pricing section, the Terms and the Plan card.
export type PaidPlanId = "starter" | "growth" | "business";
export type Plan = { id: PaidPlanId; name: string; priceUsd: number; includedAnswers: number; overagePer1000Usd: number };

// Early access (Luis, October 23, 2026), mirroring functions/lib/plans.js:
// Starter is $10 until January 1, 2027, then $19 for new subscriptions, and
// anyone subscribed before keeps $10 while they stay on Starter.
export const EARLY_ACCESS = { plan: "starter" as PaidPlanId, untilMs: Date.parse("2027-01-01T00:00:00-05:00"), priceUsd: 10, laterPriceUsd: 19, untilLabel: "January 1, 2027" };

export function earlyAccessActive(now = Date.now()): boolean {
  return now < EARLY_ACCESS.untilMs;
}

export const PLANS: Record<PaidPlanId, Plan> = {
  starter: {
    id: "starter",
    name: "Starter",
    get priceUsd() {
      return earlyAccessActive() ? EARLY_ACCESS.priceUsd : EARLY_ACCESS.laterPriceUsd;
    },
    includedAnswers: 2000,
    overagePer1000Usd: 5
  },
  growth: { id: "growth", name: "Growth", priceUsd: 49, includedAnswers: 15000, overagePer1000Usd: 4 },
  business: { id: "business", name: "Business", priceUsd: 199, includedAnswers: 75000, overagePer1000Usd: 3 }
};
export const PAID_PLAN_IDS: PaidPlanId[] = ["starter", "growth", "business"];

// Not self-serve: agreed per customer. The site quotes where it starts.
export const ENTERPRISE = { id: "enterprise", name: "Enterprise", fromUsd: 1000, overagePer1000UsdLow: 1.5, overagePer1000UsdHigh: 2.5 };

export const MAX_SPENDING_CAP_USD = 100000;

// What a bot is on, in a word: "Free", "Starter", "Growth", "Business",
// "Enterprise" or "Complimentary". A paid bot from before plans is Starter.
export function planName(agent: { paid: boolean; billing: { plan?: string; complimentary?: boolean } }): string {
  if (agent.billing.complimentary) return "Complimentary";
  const plan = agent.billing.plan || (agent.paid ? "starter" : "free");
  if (plan === "enterprise") return "Enterprise";
  if (plan === "free") return "Free";
  return PLANS[plan as PaidPlanId]?.name || "Starter";
}

// Where "Upgrade" leads: "choose" for a free bot (pick any plan), the next
// plan up for a paid one, or null at the top (Business, Enterprise,
// complimentary), where there is nothing to upgrade to.
export function upgradeTarget(agent: { paid: boolean; billing: { plan?: string; complimentary?: boolean } }): "choose" | PaidPlanId | null {
  if (agent.billing.complimentary) return null;
  const plan = agent.billing.plan || (agent.paid ? "starter" : "free");
  if (plan === "free") return "choose";
  const index = PAID_PLAN_IDS.indexOf(plan as PaidPlanId);
  return index >= 0 && index < PAID_PLAN_IDS.length - 1 ? PAID_PLAN_IDS[index + 1] : null;
}

export function upgradeLabel(target: "choose" | PaidPlanId): string {
  return target === "choose" ? "Upgrade" : `Upgrade to ${PLANS[target].name}`;
}

// "$4" or "$1.50": money as a person writes it.
export function usd(amount: number): string {
  return `$${amount.toLocaleString("en-US", { minimumFractionDigits: amount % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
}

export function answers(count: number): string {
  return count.toLocaleString("en-US");
}

// How many unpaid agents one account may hold. Mirrored because the wizard
// warns before the last step rather than letting someone fill in four steps to
// be refused; the refusal itself is the server's, in createAgent.
// One since October 26, 2026 (it was 2), mirroring functions/lib/plans.js.
export const FREE_AGENT_LIMIT = 1;

// The owner's tools on the paid plan, and a free bot's conversation history
// (functions/lib/plans.js PAID_KINDS, FREE_HISTORY_DAYS).
export const PAID_KINDS = ["booking", "slack", "discord", "webhook"] as const;
export const FREE_HISTORY_DAYS = 7;

// The cheapest plan, quoted wherever one price stands for "upgrading".
export const PAID_PRICE_USD = PLANS.starter.priceUsd;

// null is unlimited, and is a real stored value -- never 0, never Infinity.
export const PAID_LIMIT_CHOICES: (number | null)[] = [10, 20, 50, 100, null];

export function isValidPaidLimit(limit: number | null): boolean {
  return PAID_LIMIT_CHOICES.some((choice) => choice === limit);
}

export function limitLabel(limit: number | null): string {
  return limit === null ? "Unlimited" : `${limit} questions`;
}
